import FormInput from 'src/components/form-input';
import FormSelect from 'src/components/form-select';
import FormSwitch from 'src/components/form-switch';
import UserCredentialsManager from 'src/components/user-credentials-manager';
import {
  IUserCredential,
  NtpSettingsType,
  GlobalSettingsType,
} from 'src/context/global/types';

import { GlobalSettingsValidationError } from './global-settings-sections';

export const globalSettingsTabs = [
  { name: 'Unused Ports', key: 'unused-ports' },
  { name: 'Ignition', key: 'ignition' },
  { name: 'Remote Logging', key: 'remote-logging' },
  { name: 'NTP', key: 'ntp' },
  { name: 'User Credentials', key: 'user-credentials' },
] as const;

export const ntpQueryIntervalOptions = [
  { label: '8 seconds', value: 3 },
  { label: '16 seconds', value: 4 },
  { label: '32 seconds', value: 5 },
  { label: '1 minute', value: 6 },
  { label: '2 minutes', value: 7 },
  { label: '4 minutes', value: 8 },
  { label: '8 minutes', value: 9 },
  { label: '17 minutes', value: 10 },
  { label: '34 minutes', value: 11 },
  { label: '68 minutes', value: 12 },
  { label: '136 minutes', value: 13 },
  { label: '273 minutes', value: 14 },
  { label: '546 minutes', value: 15 },
  { label: '1092 minutes', value: 16 },
];

interface BaseSectionProps {
  values: GlobalSettingsType;
  onChange: (next: GlobalSettingsType) => void;
}

interface SectionWithMetaProps extends BaseSectionProps {
  dataTestIdPrefix?: string;
  showDescription?: boolean;
}

interface NtpSectionProps extends SectionWithMetaProps {
  error?: GlobalSettingsValidationError;
}

export function GlobalSettingsUnusedPortsSection({
  values,
  onChange,
  dataTestIdPrefix,
  showDescription = true,
}: SectionWithMetaProps) {
  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900">
        Unused Ports Settings
      </h2>
      {showDescription && (
        <p className="mt-1 text-sm text-gray-600">
          Settings for unconnected ports of ROQSTAR Managed Switches.
        </p>
      )}
      <div className="mt-4 flex flex-col gap-4">
        <FormSwitch
          id="unused-ports-interface"
          name="unusedPortsInterface"
          label="Link"
          data-testid={
            dataTestIdPrefix
              ? `${dataTestIdPrefix}-unused-ports-interface`
              : undefined
          }
          checked={values.unusedPorts?.interface ?? false}
          onChange={(_value) =>
            onChange({
              ...values,
              unusedPorts: { ...values.unusedPorts, interface: _value },
            })
          }
        />
        <FormSwitch
          id="unused-ports-poe"
          name="unusedPortsPoE"
          label="PoE"
          data-testid={
            dataTestIdPrefix
              ? `${dataTestIdPrefix}-unused-ports-poe`
              : undefined
          }
          checked={values.unusedPorts?.poe ?? false}
          onChange={(_value) =>
            onChange({
              ...values,
              unusedPorts: { ...values.unusedPorts, poe: _value },
            })
          }
        />
      </div>
    </div>
  );
}

export function GlobalSettingsNtpSection({
  values,
  error,
  onChange,
  dataTestIdPrefix,
  showDescription = true,
}: NtpSectionProps) {
  const handleNtpChange = (key: keyof NtpSettingsType, value: any) => {
    onChange({
      ...values,
      ntp: { ...values.ntp, [key]: value },
    });
  };

  return (
    <div className="flex flex-col gap-2 py-2">
      <h2 className="text-base font-semibold text-gray-900">NTP Settings</h2>
      {showDescription && (
        <p className="mt-1 text-sm text-gray-600">
          Configure Network Time Protocol (NTP) client settings.
        </p>
      )}
      <div className="mt-4 flex flex-col gap-4">
        <FormSwitch
          id="ntp-enabled"
          name="ntpEnabled"
          label="Enabled"
          data-testid={
            dataTestIdPrefix ? `${dataTestIdPrefix}-ntp-enabled` : undefined
          }
          checked={values.ntp?.enable || false}
          onChange={(_value) => handleNtpChange('enable', _value)}
        />
        <FormInput
          id="ntp-server"
          name="ntpServer"
          label="NTP Server"
          placeholder="hostname or IP address"
          data-testid={
            dataTestIdPrefix ? `${dataTestIdPrefix}-ntp-server` : undefined
          }
          value={values.ntp?.server || ''}
          onChange={(e) => handleNtpChange('server', e.target.value)}
          error={error?.ntpServer}
          disabled={!values.ntp?.enable}
        />
        <FormSelect
          id="ntp-query-interval"
          name="ntpQueryInterval"
          label="Query Interval"
          data-testid={
            dataTestIdPrefix
              ? `${dataTestIdPrefix}-ntp-query-interval`
              : undefined
          }
          value={values.ntp?.queryInterval?.toString() || ''}
          onChange={(e) =>
            handleNtpChange('queryInterval', parseInt(e.target.value, 10) || 12)
          }
          options={ntpQueryIntervalOptions.map((option) => ({
            label: option.label,
            value: option.value.toString(),
          }))}
          disabled={!values.ntp?.enable}
        />
      </div>
    </div>
  );
}

export function GlobalSettingsUserCredentialsSection({
  values,
  onChange,
}: BaseSectionProps) {
  const onUserCredentialsChange = (userCredentials: IUserCredential[]) => {
    onChange({ ...values, userCredentials });
  };

  return (
    <UserCredentialsManager
      userCredentials={values.userCredentials || []}
      onUserCredentialsChange={onUserCredentialsChange}
    />
  );
}
